<script setup lang="ts">
import { computed, type CSSProperties, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import type { TLoginHintBadge } from "@/api/models/user/loginHintBadge";
import { useAuthStore } from "@/stores/authStore";
import { useLoginHintBadgesInside } from "@/composables/models/user/useLoginHintBadges";

const props = withDefaults(
  defineProps<{
    compact?: boolean;
  }>(),
  {
    compact: false,
  }
);

const layout = computed(() => {
  return {
    rootWidth: 53,
    rootHeight: 30,
    hoverClosedWidth: 67,
    hoverClosedHeight: 38,
    hoverOpenWidth: 160,
    hoverOpenHeight: 76,

    mainChipWidth: 67,
    mainChipHeight: 32,
    mainChipTop: 3,
    mainChipText: "tw:text-[13px]",

    sideBaseX: 59,
    sideStepX: 23,
    sideBaseY: 8,
    sideStepY: 2,

    // No-role mode: chips sit in a tight, flat row instead of fanning around a centre chip.
    rowBaseX: 40,
    rowStepX: 40,
    rowBaseY: 8,

    bridgeWidth: 83,
    bridgeHeight: 24,
    bridgeCutWidth: 17,
    bridgeCutHeight: 20,
    bridgeCutOffsetX: 5,
    bridgeCutBottom: -1,
    bridgeRadius: 17,
  };
});

const authStore = useAuthStore();
const { mdAndUp } = useDisplay();

const hovered = ref(false);
const manualOpen = ref(false);
const switchingRole = ref(false);

const { data: loginHintBadges } = useLoginHintBadgesInside();

const currentEntry = computed<TLoginHintBadge | null>(() => {
  // Each login-hint badge is an alternate login carrying its own email, so the badge
  // whose email matches the current session uniquely identifies the role we're in.
  const emails = authStore.authUser?.logged_staff?.emails;
  const currentEmail = (
    emails?.find((email) => email.primary) ?? emails?.[0]
  )?.email?.toLowerCase();

  if (!currentEmail) {
    return null;
  }

  return (
    loginHintBadges.value?.find((badge) => badge.email?.toLowerCase() === currentEmail) ?? null
  );
});

const mainEntry = computed<TLoginHintBadge | null>(() => {
  const badges = loginHintBadges.value;

  if (!badges?.length) {
    return null;
  }

  if (currentEntry.value) {
    return currentEntry.value;
  }

  const middleIndex = Math.floor((badges.length - 1) / 2);

  return badges[middleIndex] ?? null;
});

const sideEntries = computed(() => {
  return loginHintBadges.value?.filter((badge) => badge.id !== mainEntry.value?.id) ?? [];
});

const leftEntries = computed(() => {
  return sideEntries.value.filter((_, index) => index % 2 === 0).toReversed();
});

const rightEntries = computed(() => {
  return sideEntries.value.filter((_, index) => index % 2 === 1);
});

// A user "owns" a role only when one of their groups matches a switchable badge.
// GP / ORT / TC users have a matching badge; a SHOW-LOGIN-SWITCH-only user does not.
const hasCurrentRole = computed(() => Boolean(currentEntry.value));

const isOpen = computed(() => {
  // Desktop expands on hover; users without an own role can also tap-toggle on mobile.
  return mdAndUp.value ? hovered.value || manualOpen.value : manualOpen.value;
});

// Without an own role there is nothing meaningful to show by default, so the shelf
// stays collapsed into a neutral handle until the user opens it.
const collapsed = computed(() => !hasCurrentRole.value && !isOpen.value);

const closeShelf = () => {
  hovered.value = false;
  manualOpen.value = false;
};

watch(
  () => props.compact,
  (isCompact) => {
    if (isCompact) {
      closeShelf();
    }
  }
);

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getSideChipStyle = (side: "left" | "right", index: number): CSSProperties => {
  const direction = side === "left" ? -1 : 1;

  // Without an own role there is no prominent centre chip, so pack the choices
  // into a compact horizontal row rather than fanning them out wide.
  const isRow = !hasCurrentRole.value;

  const baseX = isRow ? layout.value.rowBaseX : layout.value.sideBaseX;
  const stepX = isRow ? layout.value.rowStepX : layout.value.sideStepX;
  const baseY = isRow ? layout.value.rowBaseY : layout.value.sideBaseY;
  const stepY = isRow ? 0 : layout.value.sideStepY;

  const x = baseX + index * stepX;
  const y = baseY + index * stepY;

  return {
    transform: isOpen.value
      ? `translate(calc(-50% + ${direction * x}px), ${y}px) scale(1)`
      : "translate(-50%, 0) scale(0.6)",
    opacity: isOpen.value ? 1 : 0,
    pointerEvents: isOpen.value ? "auto" : "none",
    // Springy overshoot, staggered so chips pop out of the tab one after another.
    transition: "transform 0.36s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.22s ease",
    transitionDelay: isOpen.value ? `${index * 45}ms` : "0ms",
  };
};

const switchRole = async (badgeId: number) => {
  const badge = loginHintBadges.value?.find((badge) => badge.id === badgeId);

  if (switchingRole.value || badgeId === currentEntry.value?.id || !badge) {
    return;
  }

  try {
    switchingRole.value = true;
    await authStore.switchToLoginHintBadge(badge);
    await wait(3000);
    closeShelf();
  } finally {
    authStore.finishRoleSwitch();
    switchingRole.value = false;
  }
};

const hoverAreaStyle = computed<CSSProperties>(() => ({
  width: `${isOpen.value ? layout.value.hoverOpenWidth : layout.value.hoverClosedWidth}px`,
  height: `${isOpen.value ? layout.value.hoverOpenHeight : layout.value.hoverClosedHeight}px`,
}));

const leftCutStyle = computed<CSSProperties>(() => ({
  width: `${layout.value.bridgeCutWidth}px`,
  height: `${layout.value.bridgeCutHeight}px`,
  bottom: `${layout.value.bridgeCutBottom}px`,
  left: `-${layout.value.bridgeCutOffsetX}px`,
  borderTopRightRadius: `${layout.value.bridgeRadius}px`,
  background: "rgb(var(--v-theme-surface))",
}));

const rightCutStyle = computed<CSSProperties>(() => ({
  width: `${layout.value.bridgeCutWidth}px`,
  height: `${layout.value.bridgeCutHeight}px`,
  bottom: `${layout.value.bridgeCutBottom}px`,
  right: `-${layout.value.bridgeCutOffsetX}px`,
  borderTopLeftRadius: `${layout.value.bridgeRadius}px`,
  background: "rgb(var(--v-theme-surface))",
}));

const roleBridgeStyle = computed<CSSProperties>(() => ({
  width: `${layout.value.bridgeWidth}px`,
  height: `${layout.value.bridgeHeight}px`,
  background: mainEntry.value?.color,
}));

// No-role mode: a primary-tinted tray sitting under the row of choices, tying them
// visually to the (primary-coloured) opening tab. Springs in together with the chips.
const rowBackdropStyle = computed<CSSProperties>(() => {
  const sideCount = Math.max(leftEntries.value.length, rightEntries.value.length);
  const maxX = sideCount ? layout.value.rowBaseX + (sideCount - 1) * layout.value.rowStepX : 0;
  const halfWidth = maxX + 42;

  return {
    width: `${halfWidth * 2}px`,
    height: "38px",
    background: "rgba(var(--v-theme-background), 0.85)",
    border: "1px solid rgb(var(--v-theme-background))",
    transformOrigin: "top center",
    opacity: isOpen.value ? 1 : 0,
    transform: isOpen.value ? "translateX(-50%) scaleY(1)" : "translateX(-50%) scaleY(0.6)",
    transition: "transform 0.36s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.22s ease",
  };
});

const shelfStyle = computed<CSSProperties>(() => ({
  transform: props.compact ? "translate(0, -100px)" : "translate(0, -4px)",
  pointerEvents: props.compact ? "none" : "auto",
}));
</script>

<template>
  <div
    v-if="loginHintBadges?.length"
    class="tw:fixed tw:top-0 tw:left-0 tw:z-layer-70 tw:w-full tw:transition-transform tw:duration-300"
    :style="shelfStyle"
  >
    <div
      v-if="!compact && mainEntry && hasCurrentRole"
      class="tw:absolute tw:left-0 tw:top-0 tw:z-layer-70 tw:h-[4px] tw:w-screen tw:opacity-95"
      :style="{ background: mainEntry.color }"
    />

    <div
      class="tw:absolute tw:left-1/2 tw:-top-0.25 tw:z-sticky-header tw:-translate-x-1/2 tw:flex tw:items-start tw:justify-center"
      :style="hoverAreaStyle"
      @mouseenter="hovered = true"
      @mouseleave="closeShelf"
    >
      <button
        v-if="collapsed"
        type="button"
        class="tw:pointer-events-auto tw:absolute tw:left-1/2 tw:top-0 tw:-translate-x-1/2 tw:border-0 tw:p-0 tw:bg-transparent tw:z-layer-70"
        :disabled="switchingRole"
        @click="manualOpen = !manualOpen"
      >
        <div class="role-handle">
          <v-icon
            size="16"
            class="tw:text-white"
          >
            mdi-account-switch
          </v-icon>
          <v-icon
            size="14"
            class="tw:text-white tw:opacity-80"
          >
            mdi-chevron-down
          </v-icon>
        </div>
      </button>

      <div
        class="tw:pointer-events-none tw:relative tw:flex tw:h-[32px] tw:w-full tw:justify-center"
      >
        <span
          v-if="!hasCurrentRole"
          class="tw:pointer-events-none tw:absolute tw:left-1/2 tw:top-[2px] tw:rounded-t-[6px] tw:rounded-b-[18px]"
          :style="rowBackdropStyle"
        />

        <button
          v-for="(role, index) in leftEntries"
          :key="`left-${role.id}`"
          type="button"
          class="tw:pointer-events-auto tw:absolute tw:left-1/2 tw:top-0 tw:rounded-full tw:border-0 tw:bg-transparent tw:p-0 tw:z-layer-21"
          :style="getSideChipStyle('left', index)"
          :disabled="switchingRole"
          @click="switchRole(role.id)"
        >
          <v-chip
            :color="role.color"
            size="x-small"
            variant="flat"
            class="tw:min-h-[24px] tw:min-w-[35px] tw:text-white tw:px-2 tw:text-[12px] tw:leading-none tw:items-center tw:justify-center tw:shadow-sm"
          >
            {{ role.name }}
          </v-chip>
        </button>

        <button
          v-for="(role, index) in rightEntries"
          :key="`right-${role.id}`"
          type="button"
          class="tw:pointer-events-auto tw:absolute tw:left-1/2 tw:top-0 tw:rounded-full tw:border-0 tw:bg-transparent tw:p-0 tw:z-layer-21"
          :style="getSideChipStyle('right', index)"
          :disabled="switchingRole"
          @click="switchRole(role.id)"
        >
          <v-chip
            :color="role.color"
            size="x-small"
            variant="flat"
            class="tw:min-h-[24px] tw:min-w-[35px] tw:text-white tw:px-2 tw:text-[12px] tw:leading-none tw:items-center tw:justify-center tw:shadow-sm"
          >
            {{ role.name }}
          </v-chip>
        </button>

        <button
          type="button"
          class="tw:absolute tw:left-1/2 tw:top-0 tw:z-layer-20 tw:-translate-x-1/2"
          :style="{ pointerEvents: hasCurrentRole || isOpen ? 'auto' : 'none' }"
          @click="switchRole(mainEntry?.id ?? 0)"
        >
          <div class="tw:relative tw:flex tw:justify-center">
            <div
              v-if="hasCurrentRole"
              class="role-bridge"
              :style="roleBridgeStyle"
            >
              <span
                class="role-bridge-cut role-bridge-cut--left"
                :style="leftCutStyle"
              />
              <span
                class="role-bridge-cut role-bridge-cut--right"
                :style="rightCutStyle"
              />
            </div>

            <!-- Own role: prominent "selected" chip anchored by the bridge. -->
            <v-chip
              v-if="mainEntry && hasCurrentRole"
              :color="mainEntry.color"
              variant="flat"
              class="tw:relative tw:rounded-t-[0px] tw:rounded-b-[25px] tw:flex tw:items-center tw:justify-center tw:shadow-none tw:px-4 tw:font-semibold tw:text-white"
              :class="layout.mainChipText"
              :style="{
                top: `${layout.mainChipTop}px`,
                minWidth: `${layout.mainChipWidth}px`,
                height: `${layout.mainChipHeight}px`,
              }"
            >
              {{ mainEntry.name }}
            </v-chip>

            <!-- No own role: render the centre badge as just another choice that
                 springs in from the tab alongside the side options. -->
            <v-chip
              v-else-if="mainEntry"
              :color="mainEntry.color"
              size="x-small"
              variant="flat"
              class="tw:relative tw:mt-[8px] tw:min-h-[24px] tw:min-w-[35px] tw:text-white tw:px-2 tw:text-[12px] tw:leading-none tw:items-center tw:justify-center tw:shadow-sm"
              :style="{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? 'scale(1)' : 'scale(0.6)',
                transition: 'transform 0.36s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.22s ease',
              }"
            >
              {{ mainEntry.name }}
            </v-chip>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.role-bridge {
  position: absolute;
  top: 0;
  z-index: var(--z-index-base);
}

.role-bridge-cut {
  position: absolute;
}

.role-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 48px;
  height: 28px;
  padding-bottom: 3px;
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition:
    height 0.2s ease,
    opacity 0.2s ease;
}

.role-handle:hover {
  height: 32px;
  opacity: 0.92;
}
</style>
